/**
 * 表格工具栏组件 — 列表页顶部的总数显示和刷新按钮。
 *
 * 主要导出：`CrmTableToolbar`（自定义元素 `<crm-table-toolbar>`）
 *
 * 功能特性：
 * - 显示记录总数（如 "共 100 条"）
 * - 提供刷新按钮，点击后派发 `crm-toolbar-refresh` 事件
 * - 加载中状态时禁用刷新按钮
 *
 * 注意：此组件直接继承 `LitElement`（非 BaseElement），样式自包含。
 *
 * 用法：
 *   <crm-table-toolbar .total=${100} .loading=${false} @crm-toolbar-refresh=${() => this.loadData()}>
 *   </crm-table-toolbar>
 */
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('crm-table-toolbar')
export class CrmTableToolbar extends LitElement {
  /** 总记录数 */
  @property({ type: Number })
  total = 0;

  /** 记录单位文本，默认 '条' */
  @property({ type: String })
  label = '条';

  /** 是否加载中（禁用刷新按钮） */
  @property({ type: Boolean })
  loading = false;

  static styles = css`
    :host { display: flex; }
    .toolbar {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    /* 总数文本 */
    .count {
      font-size: 13px;
      color: var(--tw-color-text-muted, #b0b4ba);
      font-weight: 500;
    }
    /* 刷新按钮 */
    .btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border: 1px solid var(--tw-color-border, #e0e1e6);
      background: var(--tw-color-surface, #ffffff);
      color: var(--tw-color-text-secondary, #60646c);
      font-size: 13px;
      font-weight: 500;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;
    }
    .btn:hover {
      background: var(--tw-color-bg, #f0f0f3);
      color: var(--tw-color-text, #1c2024);
    }
    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .btn svg {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  `;

  /**
   * 刷新按钮点击处理 — 派发 `crm-toolbar-refresh` 事件。
   * @sideeffect 派发自定义事件，由父组件监听后执行数据刷新
   */
  private handleRefresh() {
    this.dispatchEvent(new CustomEvent('crm-toolbar-refresh', { bubbles: true, composed: true }));
  }

  /**
   * 渲染工具栏。
   * 布局：总数文本 + 刷新按钮（带刷新图标）。
   * @returns Lit 模板
   */
  render() {
    return html`
      <div class="toolbar">
        <span class="count">共 ${this.total} ${this.label}</span>
        <button class="btn" ?disabled=${this.loading} @click=${this.handleRefresh}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/></svg>
          刷新
        </button>
      </div>
    `;
  }
}